import { animate, inView } from "vendor";

class CountdownTimer extends HTMLElement {
  constructor() {
    super();
    this._flips = undefined;
    this._expirationDate = undefined;
    this._interval = undefined;
    this._isVisible = undefined;
  }

  connectedCallback() {
    this._flips = Array.from(this.querySelectorAll("countdown-timer-flip"));

    const expiresAt = this.getAttribute("expires-at");
    if (expiresAt !== "" && expiresAt !== null) {
      const expirationTime = parseInt(expiresAt, 10) * 1000;
      if (!isNaN(expirationTime)) {
        this._expirationDate = new Date(expirationTime);
        this._interval = setInterval(() => this.recalculateFlips(), 1000);
        this._isVisible = true;
        this.recalculateFlips();
      }
    }

    inView(
      this,
      () => {
        this._isVisible = true;
        return () => {
          this._isVisible = false;
        };
      },
      { margin: "500px" }
    );
  }

  disconnectedCallback() {
    clearInterval(this._interval);
  }

  recalculateFlips() {
    const dateNow = new Date();

    if (this._expirationDate < dateNow) {
      if (this.getAttribute("expiration-behavior") === "hide") {
        this.closest(".shopify-section").remove();
      } else {
        this.daysFlip?.updateValue(0);
        this.hoursFlip?.updateValue(0);
        this.minutesFlip?.updateValue(0);
        this.secondsFlip?.updateValue(0);
        this.style.opacity = "1";

        const expiredScope = this.closest(".shopify-section") || this.parentElement;
        const expiredTemplate = expiredScope?.querySelector("template[data-countdown-expired-content]");
        if (expiredTemplate) {
          const contentEl = expiredScope.querySelector(".countdown__content");
          if (contentEl) contentEl.innerHTML = expiredTemplate.innerHTML;
        }

        return clearInterval(this._interval);
      }
    }

    if (!this._isVisible) return;

    let delta = Math.abs(this._expirationDate - dateNow) / 1000;
    const useTotalHours = this.hasAttribute("data-total-hours");
    let days, hours, overflow = false;

    if (useTotalHours) {
      days = 0;
      hours = Math.floor(delta / 3600);
      delta -= hours * 3600;

      if (hours > 99) {
        hours = 99;
        overflow = true;
      }
    } else {
      days = Math.floor(delta / 86400);
      delta -= days * 86400;
      hours = Math.floor(delta / 3600);
      delta -= hours * 3600;
    }

    const minutes = Math.floor(delta / 60) % 60;
    delta -= minutes * 60;
    const seconds = Math.floor(delta % 60);

    this.daysFlip?.updateValue(days);
    this.hoursFlip?.updateValue(hours);
    this.minutesFlip?.updateValue(minutes);
    this.secondsFlip?.updateValue(seconds);

    const overflowEl = this.querySelector(".countdown__timer-overflow");
    if (overflowEl) {
      if (overflow) overflowEl.removeAttribute("hidden");
      else overflowEl.setAttribute("hidden", "");
    }

    this.style.opacity = "1";
  }

  get daysFlip() {
    return this._flips.find((flip) => flip.getAttribute("type") === "days");
  }
  get hoursFlip() {
    return this._flips.find((flip) => flip.getAttribute("type") === "hours");
  }
  get minutesFlip() {
    return this._flips.find((flip) => flip.getAttribute("type") === "minutes");
  }
  get secondsFlip() {
    return this._flips.find((flip) => flip.getAttribute("type") === "seconds");
  }
}

class CountdownTimerFlip extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    const flipHtml = [...this.textContent]
      .map(
        () =>
          `<countdown-timer-flip-digit part="digit" ${
            this.hasAttribute("animate") ? "animate" : ""
          } style="display: inline-block">0</countdown-timer-flip-digit>`
      )
      .join("");
    this.shadowRoot.appendChild(document.createRange().createContextualFragment(flipHtml));
  }

  updateValue(value) {
    const max = Math.pow(10, this.shadowRoot.children.length) - 1;
    const padded = Math.max(0, Math.min(max, value))
      .toString()
      .padStart(this.shadowRoot.children.length, "0");
    this.textContent = padded;

    [...padded].forEach((digit, index) => {
      this.shadowRoot.children[index].setAttribute("number", digit);
    });
  }
}

class CountdownTimerFlipDigit extends HTMLElement {
  static observedAttributes = ["number"];

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(
      document.createRange().createContextualFragment("<div><slot></slot></div>")
    );
  }

  async attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === null || oldValue === newValue || !this.hasAttribute("animate")) {
      return (this.textContent = newValue);
    }

    await animate(
      this.shadowRoot.firstElementChild,
      { opacity: [1, 0], transform: ["translateY(0)", "translateY(-8px)"] },
      { duration: 0.3, easing: [0.64, 0, 0.78, 0] }
    ).finished;

    this.textContent = newValue;

    animate(
      this.shadowRoot.firstElementChild,
      { opacity: [0, 1], transform: ["translateY(8px)", "translateY(0px)"] },
      { duration: 0.3, easing: [0.22, 1, 0.36, 1] }
    );
  }
}

if (!window.customElements.get("countdown-timer")) {
  window.customElements.define("countdown-timer", CountdownTimer);
}
if (!window.customElements.get("countdown-timer-flip")) {
  window.customElements.define("countdown-timer-flip", CountdownTimerFlip);
}
if (!window.customElements.get("countdown-timer-flip-digit")) {
  window.customElements.define("countdown-timer-flip-digit", CountdownTimerFlipDigit);
}
